/**
 * Shared resize and retry scheduling for bar-chart DOM measurements.
 */

import { useEffect, useRef, useState } from 'react';

const MEASURE_DEBOUNCE_MS = 80;
const MAX_MEASURE_ATTEMPTS = 20;
const MEASURE_RETRY_DELAY_MS = 50;

interface UseResizeMeasureSignalOptions {
  element: Element | null;
  enabled?: boolean;
}

interface ScheduleRetryingMeasureOptions {
  measure: () => boolean;
  maxAttempts?: number;
  retryDelayMs?: number;
}

/**
 * Debounces ResizeObserver callbacks into a monotonic measure-request id.
 * Consumers re-run layout measurement when the returned id changes.
 */
export const useResizeMeasureSignal = ({
  element,
  enabled = true,
}: UseResizeMeasureSignalOptions): number => {
  const resizeRafRef = useRef<number | null>(null);
  const resizeTimeoutRef = useRef<number | null>(null);
  const [measureRequestId, setMeasureRequestId] = useState(0);

  useEffect(() => {
    if (!enabled || !element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const requestMeasure = () => {
      setMeasureRequestId((previous) => previous + 1);
    };

    requestMeasure();

    const observer = new ResizeObserver(() => {
      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = window.setTimeout(() => {
        resizeTimeoutRef.current = null;

        if (resizeRafRef.current !== null) {
          return;
        }

        resizeRafRef.current = window.requestAnimationFrame(() => {
          resizeRafRef.current = null;
          requestMeasure();
        });
      }, MEASURE_DEBOUNCE_MS);
    });

    observer.observe(element);

    return () => {
      observer.disconnect();

      if (resizeTimeoutRef.current !== null) {
        window.clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = null;
      }

      if (resizeRafRef.current !== null) {
        window.cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, [element, enabled]);

  return measureRequestId;
};

/**
 * Runs `measure` on the next animation frame, retrying until it returns true or
 * `maxAttempts` is reached. Returns a cancel function for effect cleanup.
 */
export const scheduleRetryingMeasure = ({
  measure,
  maxAttempts = MAX_MEASURE_ATTEMPTS,
  retryDelayMs = MEASURE_RETRY_DELAY_MS,
}: ScheduleRetryingMeasureOptions): (() => void) => {
  let cancelled = false;
  let frameId: number | null = null;
  let timeoutId: number | null = null;
  let attempt = 0;

  const runMeasure = () => {
    if (cancelled || measure()) {
      return;
    }

    attempt += 1;

    if (attempt >= maxAttempts) {
      return;
    }

    timeoutId = window.setTimeout(() => {
      timeoutId = null;
      frameId = window.requestAnimationFrame(runMeasure);
    }, retryDelayMs);
  };

  frameId = window.requestAnimationFrame(runMeasure);

  return () => {
    cancelled = true;

    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }

    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
    }
  };
};
